import styled from '@emotion/styled';

// ── Page shell ───────────────────────────────────────────────────────────────

export const LegalShell = styled('div')`
  min-height: 100%;
  background: var(--color-bg-body);
  padding: var(--spacing-12) var(--spacing-6) var(--spacing-16);

  @media (max-width: 768px) {
    padding: var(--spacing-8) var(--spacing-4) var(--spacing-12);
  }
`;

export const LegalContainer = styled('article')`
  max-width: 760px;
  margin: 0 auto;
`;

export const LegalTocHint = styled('p')`
  font-size: 13px;
  color: var(--color-text-disabled);
  margin: 0 0 var(--spacing-6);
  text-align: right;
  font-style: italic;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

// ── Rendered markdown ────────────────────────────────────────────────────────
//
// We scope all markdown styling to this wrapper so the rest of the app stays
// unaffected. react-markdown emits plain semantic HTML elements — this lets
// us style by tag without component overrides.

export const LegalContent = styled('div')`
  color: var(--color-text-primary);
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.7;

  /* ── Top-of-page title block ───────────────────────────────────────────── */
  /* Legal docs start with **Vellum** / **Title** / Effective Date as bold
     paragraphs — render the first bold lines prominently without needing
     them to be h1s in the source. */
  > p:first-of-type {
    font-family: var(--font-heading);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-text-disabled);
    margin: 0 0 var(--spacing-2);
  }
  > p:first-of-type + p {
    font-family: var(--font-heading);
    font-size: 34px;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-2);
  }
  > p:first-of-type + p + p {
    font-size: 14px;
    color: var(--color-text-disabled);
    margin: 0 0 var(--spacing-8);
  }

  /* ── Headings ──────────────────────────────────────────────────────────── */
  h1,
  h2,
  h3,
  h4 {
    font-family: var(--font-heading);
    color: var(--color-text-primary);
    letter-spacing: -0.01em;
    line-height: 1.25;
  }
  h1 {
    font-size: 26px;
    font-weight: 700;
    margin: var(--spacing-10) 0 var(--spacing-4);
  }
  h2 {
    font-size: 21px;
    font-weight: 700;
    margin: var(--spacing-8) 0 var(--spacing-3);
  }
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: var(--spacing-6) 0 var(--spacing-2);
  }
  h4 {
    font-size: 16px;
    font-weight: 600;
    margin: var(--spacing-5) 0 var(--spacing-2);
  }

  /* ── Body ──────────────────────────────────────────────────────────────── */
  p {
    margin: 0 0 var(--spacing-4);
    color: var(--color-text-secondary);
  }

  strong {
    color: var(--color-text-primary);
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  /* ── Lists ─────────────────────────────────────────────────────────────── */
  ul,
  ol {
    margin: 0 0 var(--spacing-4);
    padding-left: var(--spacing-6);
    color: var(--color-text-secondary);
  }
  li {
    margin-bottom: var(--spacing-2);
  }
  li > p {
    margin: 0;
  }

  /* ── Links ─────────────────────────────────────────────────────────────── */
  a {
    color: var(--color-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: opacity var(--duration-shortest) ease;
    &:hover {
      opacity: 0.8;
    }
  }

  /* ── Block elements ────────────────────────────────────────────────────── */
  hr {
    border: none;
    border-top: 1px solid var(--color-border-default);
    margin: var(--spacing-8) 0;
  }

  blockquote {
    border-left: 3px solid var(--color-primary);
    background: var(--color-bg-elevated);
    padding: var(--spacing-3) var(--spacing-5);
    margin: var(--spacing-4) 0;
    color: var(--color-text-secondary);
    font-style: italic;
  }

  code {
    font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
    font-size: 0.92em;
    background: var(--color-bg-elevated);
    padding: 1px 6px;
    border-radius: var(--radius-sm);
  }

  /* ── Tables (enabled by remark-gfm) ────────────────────────────────────── */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: var(--spacing-4) 0;
    font-size: 14px;
  }
  th,
  td {
    border: 1px solid var(--color-border-default);
    padding: var(--spacing-2) var(--spacing-3);
    text-align: left;
  }
  th {
    background: var(--color-bg-elevated);
    color: var(--color-text-primary);
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    > p:first-of-type + p {
      font-size: 28px;
    }
    h1 {
      font-size: 22px;
    }
    h2 {
      font-size: 18px;
    }
  }
`;
